<script setup lang="ts">
/**
 * The Upcoming tile: the one slot that always shows the single most important thing the user
 * can do right now (IPD-2976 10.1).
 *
 * Six faces, as the ticket enumerates them. Figma draws five of them across ten instances,
 * because the recommendation is shown once per content type and the task once per task type —
 * those are the same face with different content, not faces of their own.
 *
 * Underneath, the six collapse into two layouts: heading + body + inline CTA (welcome, nothing
 * due, idle) and heading + body + card (recommendation, task). The sixth, all-done, is drawn
 * nowhere: it borrows the idle layout until design provides it.
 *
 * The tile does NOT choose its face. The face arrives resolved in the payload and is rendered;
 * the only thing decided here is the greeting band, which comes from the device clock (10.3).
 */
import {computed, onMounted, onUnmounted, ref} from 'vue';
import {Translation as I18nT} from 'vue-i18n';
import TodayItemCard from '@/vue/molecules/today/today-item-card.vue';
import FormattedDate from '@/vue/atoms/formatted-date.vue';
import {
  type TodayCardItem,
  type TodayNextTask,
  type TodayOnboardingQuestionnaire,
  type TodayUpcoming,
  TodayUpcomingFace,
} from '@/ts/types/component/today-page';
import {resolveTodayGreeting, todayGreetingKey} from '@/ts/composables/pure/use-today-greeting';
import {pagesPath} from '@/ts/router/pages-path';
import placeholderAvatar from '@/assets/img/Avatar.png';

const props = withDefaults(defineProps<{
  upcoming: TodayUpcoming;
  /** First name. The design greets by name in every face, including the welcome one. */
  userName: string;
  /**
   * The user's picture, shown above the greeting as the design has it. Nothing in the product
   * produces one yet — no field, no column, no upload — so it falls back to the same placeholder
   * the header's user menu uses, and the tile is ready for the real thing when it exists.
   */
  avatarUrl?: string | null;
  /** Injectable so the greeting boundaries are assertable; production always uses the clock. */
  now?: Date;
}>(), {
  avatarUrl: null,
});

const emit = defineEmits<{
  /**
   * The welcome CTA, carrying the questionnaire the payload sent for it: the onboarding
   * questionnaire opens in the existing modal, not a route, so the handler needs its id.
   */
  startOnboarding: [questionnaire: TodayOnboardingQuestionnaire];
  /** The card was activated. Modal items rely on this; links navigate on their own. */
  selectItem: [item: TodayCardItem];
  /**
   * The nudge towards Explore, on the face that has nothing of the user's own to offer. Announced
   * although it is a link, for the same reason the card announces: otherwise the one face whose
   * only purpose is to send the user somewhere would be the one that never says it did.
   */
  browse: [];
}>();

/**
 * The clock the greeting reads. It cannot be a prop default: Vue evaluates a default factory once
 * and caches it for the component's lifetime, so `new Date()` there would freeze the band at
 * mount — a tab left open since the morning would still say "Good morning" at night.
 *
 * Instead it is refreshed whenever the tab becomes visible again, which is when a stale greeting
 * would actually be read. A user staring at the page as the clock strikes five keeps the old band
 * until they look away and back, which is a fair trade for not running a timer all day.
 */
const clock = ref<Date>(props.now ?? new Date());

function refreshClock(): void {
  if (!props.now && document.visibilityState === 'visible') {
    clock.value = new Date();
  }
}

onMounted(() => {
  document.addEventListener('visibilitychange', refreshClock);
});
onUnmounted(() => {
  document.removeEventListener('visibilitychange', refreshClock);
});

const greetingKey = computed<string>(() => todayGreetingKey[resolveTodayGreeting(props.now ?? clock.value)]);

/**
 * Two faces replace the time-of-day greeting with a heading of their own: the first-time user
 * is welcomed whatever the hour, and the just-onboarded one is congratulated. Everyone else is
 * greeted by the clock, the suggestion face included — it draws the same card as the
 * congratulation, under the ordinary greeting. The three headings share one slot in the design,
 * never stack.
 */
const headingKey = computed<string>(() => {
  switch (props.upcoming.face) {
    case TodayUpcomingFace.welcome: {
      return 'today_upcoming_welcome_title';
    }
    case TodayUpcomingFace.recommendation: {
      return 'today_upcoming_recommendation_title';
    }
    default: {
      return greetingKey.value;
    }
  }
});

/**
 * Whether the heading can be drawn yet. Four of the five headings greet by name, and interpolating
 * a blank one gives "Good afternoon, ." Not the in-flight case: `main-base-page` wraps this subtree
 * in `loading-error`, whose default slot only renders once `/me` has resolved, so the tile cannot
 * mount without user data. What is left is a name that arrives empty, which the store's type does
 * not rule out. Holding the line invents no copy: there is no nameless variant of the greetings,
 * and writing one is the copy ticket's (IPD-2982).
 *
 * The rest of the intro still draws, so the tile is never blank while it waits.
 */
const headingReady = computed<boolean>(() =>
  props.upcoming.face === TodayUpcomingFace.recommendation || props.userName.trim().length > 0);

const bodyKey = computed<string>(() => {
  switch (props.upcoming.face) {
    case TodayUpcomingFace.welcome: {
      return 'today_upcoming_welcome_body';
    }
    case TodayUpcomingFace.recommendation: {
      return 'today_upcoming_recommendation_body';
    }
    case TodayUpcomingFace.task: {
      return 'today_upcoming_task_body';
    }
    // Neither a dateless nothing-due nor a nameless one can arrive: the type requires both, and a
    // payload missing either is downgraded to the idle face by the mapper before it reaches here.
    case TodayUpcomingFace.nothingDue: {
      return 'today_upcoming_nothing_due_body';
    }
    // A suggestion the user did not earn today: the same card as above under a plain line, since
    // the "Awesome! That gives us a first idea…" copy answers having just filled in the onboarding
    // questionnaire and nothing else.
    case TodayUpcomingFace.suggestion: {
      return 'today_upcoming_suggestion_body';
    }
    default: {
      return 'today_upcoming_idle_body';
    }
  }
});

const card = computed<TodayCardItem | null>(() => {
  if (props.upcoming.face === TodayUpcomingFace.task) {
    return props.upcoming.task;
  }
  if (
    props.upcoming.face === TodayUpcomingFace.recommendation
    || props.upcoming.face === TodayUpcomingFace.suggestion
  ) {
    return props.upcoming.recommendation;
  }
  return null;
});

const unlockAt = computed<string | null>(() =>
  props.upcoming.face === TodayUpcomingFace.nothingDue ? props.upcoming.nextUnlockAt : null);

/** What the date belongs to. Null on every other face, which is every face without a date. */
const nextTask = computed<TodayNextTask | null>(() =>
  (props.upcoming.face === TodayUpcomingFace.nothingDue ? props.upcoming.nextTask : null));

/**
 * The programme above the sentence, as the task face draws it on its card. Dropped when the CMS
 * left the session unnamed and the programme is standing in for it, so the same words are not
 * printed twice.
 */
const nextTaskParent = computed<string | null>(() =>
  (nextTask.value && nextTask.value.parentName !== nextTask.value.title
    ? nextTask.value.parentName
    : null));

// Not on the suggestion face, which used to be here when it had nothing to draw: it now carries a
// card, and the two faces that already draw one send the user into it rather than off to Explore.
const showExploreCta = computed<boolean>(() =>
  props.upcoming.face === TodayUpcomingFace.nothingDue
  || props.upcoming.face === TodayUpcomingFace.idle);

// Narrowed here rather than left as the template's `v-else`, for two reasons: the onboarding
// button belongs to the welcome face and nothing else, so a task face that somehow arrives
// without its task shows no action at all instead of inviting an onboarded user back into the
// onboarding questionnaire; and the button needs the questionnaire itself, which only exists on
// that face of the union.
const onboardingQuestionnaire = computed<TodayOnboardingQuestionnaire | null>(() =>
  props.upcoming.face === TodayUpcomingFace.welcome ? props.upcoming.questionnaire : null);
</script>

<template>
  <section class="upcoming" :data-face="upcoming.face">
    <!-- Heading and body are one block: the design keeps them close together and puts its air
         between them and the action below, not between the two lines of copy. -->
    <div class="upcoming__intro">
      <img :src="avatarUrl ?? placeholderAvatar" class="upcoming__avatar" alt="" aria-hidden="true"/>

      <h2 v-if="headingReady" class="upcoming__heading">
        {{ $t(headingKey, {name: userName}) }}
      </h2>

      <p v-if="nextTaskParent" class="upcoming__eyebrow">
        {{ nextTaskParent }}
      </p>

      <!-- The unlock date is emphasised inside the sentence, so the sentence has to be
           assembled around the formatted date rather than around a pre-formatted string. The
           session's own numbers go in the same way, for the same reason. -->
      <i18n-t v-if="unlockAt" :keypath="bodyKey" tag="p" class="upcoming__body">
        <template #number>
          {{ nextTask?.sessionNumber }}
        </template>
        <template #total>
          {{ nextTask?.totalSessions }}
        </template>
        <template #title>
          {{ nextTask?.title }}
        </template>
        <template #date>
          <strong class="upcoming__date"><formatted-date :date="unlockAt" format="dayAndMonth"/></strong>
        </template>
      </i18n-t>
      <p v-else class="upcoming__body">
        {{ $t(bodyKey) }}
      </p>
    </div>

    <today-item-card
      v-if="card"
      :item="card"
      class="upcoming__card"
      @select="emit('selectItem', $event)"
    />

    <router-link
      v-else-if="showExploreCta"
      :to="pagesPath.main.explore"
      class="upcoming__cta"
      @click="emit('browse')"
    >
      <span>{{ $t('today_upcoming_explore_cta') }}</span>
      <q-icon name="arrow_forward" size="24px"/>
    </router-link>

    <button
      v-else-if="onboardingQuestionnaire"
      type="button"
      class="upcoming__cta"
      @click="emit('startOnboarding', onboardingQuestionnaire)"
    >
      <span>{{ $t('today_upcoming_welcome_cta') }}</span>
      <q-icon name="arrow_forward" size="24px"/>
    </button>
  </section>
</template>

<style scoped lang="scss">
// The tile takes its padding and its heading voice from the page's tokens, so every tile of the
// bento keeps the same rhythm. The gap is the design's own: copy and action sit far apart, which
// is what gives the tile its air.
.upcoming {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-6);
  padding: var(--today-tile-padding);
  border-radius: var(--today-radius-section);
  background: var(--today-surface-raised);
}

.upcoming__intro {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);
}

// Same size and shape as the header's user menu avatar, so the two read as the same person.
.upcoming__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}

.upcoming__heading {
  @include font-today-tile-title;

  margin: 0;
  color: var(--Neutral-UI-neutral-ui-100);
}

// The programme the waiting session belongs to, drawn as the item card draws it above a title:
// same caption step, same tone, so the two places that name a parent read alike.
.upcoming__eyebrow {
  @include font-caption-sm;

  font-size: var(--today-text-card-eyebrow);
  margin: 0;
  color: var(--Neutral-UI-neutral-ui-80);

  // Same reason as the body below: this is a CMS name, and a single unbroken one would push the
  // tile wider than its column.
  overflow-wrap: anywhere;
}

// The design sets this line at 18px, a step above the app's body size — which is the size
// font-h3 carries. Borrowing the heading mixin for a paragraph reads odd, but it is the same
// family and weight, and inventing a second Today-only size token for one line reads worse.
.upcoming__body {
  @include font-h3;

  font-size: var(--today-text-lead);
  margin: 0;
  color: var(--Neutral-UI-neutral-ui-100);

  // The nothing-due and idle sentences carry a newline: the design sets what is waiting and the
  // nudge towards Explore on separate lines. Honouring it takes pre-line rather than pre-wrap,
  // so the copy still wraps on its own when the tile is too narrow to hold either line.
  white-space: pre-line;

  // CMS titles run long, German ones longer, and this sentence carries one inline. Breaking
  // inside a word is what keeps a single unbroken title from pushing the tile wider than its
  // column; the sentence wraps as prose and the tile keeps its shape.
  overflow-wrap: anywhere;

  // One word to a reader, so the wrapping above does not get to split it. A long title on a narrow
  // tile pushes the date to the end of a line, and without this it reads "unlocks on Tue, Aug" /
  // "25." — the one place in the sentence where a break changes what it says.
  .upcoming__date {
    white-space: nowrap;
  }
}

// The card and the CTA are the tile's action, and they sit at the bottom whatever the length
// of the copy above — otherwise the four faces bounce the button around as you move between them.
.upcoming__card,
.upcoming__cta {
  margin-top: auto;
}

.upcoming__cta {
  @include focus-visible;
  @include font-body-bold;

  // This tile's own size rather than the shared pill one: the prototype draws this CTA at the size
  // of the sentence above it, and the two other tiles that carry a call to action at two smaller
  // sizes of their own. It floors at the app's minimum font size; the mixin above keeps supplying
  // family, weight and line-height.
  font-size: var(--today-text-cta-lead);

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--Spacing-spacing-4);
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  text-align: left;
  text-decoration: none;
  color: var(--Neutral-UI-neutral-ui-100);
  cursor: pointer;

  // The arrow leans the way it is about to take the user, while the pointer is anywhere in the
  // tile rather than on the arrow itself: the whole tile is the target here, and this is what says
  // where the press goes. Off the design's prototype.
  //
  // Only the icon moves. Nudging the row would slide the label as well, and the label is the left
  // edge of a line the design keeps still.
  :deep(.q-icon) {
    transition: transform 0.2s ease;
  }
}

@media (hover: hover) {
  .upcoming:hover .upcoming__cta :deep(.q-icon) {
    transform: translateX(4px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .upcoming__cta :deep(.q-icon),
  .upcoming:hover .upcoming__cta :deep(.q-icon) {
    transition: none;
    transform: none;
  }
}
</style>
